import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Turnstile } from '@marsidev/react-turnstile';
import { FaMapMarkerAlt, FaInstagram, FaTwitter, FaCheck } from 'react-icons/fa';
import './ContactPage.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  const subjects = [
    'General Inquiry',
    'Dive Trip Advice',
    'Collaboration',
    'Other',
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required.';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.subject) newErrors.subject = 'Please select a subject.';
    if (!formData.message.trim()) newErrors.message = 'Message is required.';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!turnstileToken) {
      setSubmitError('Please complete the captcha.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, turnstileToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSubmitError(data.error || 'Something went wrong. Please try again.');
        turnstileRef.current?.reset();
        setTurnstileToken('');
        return;
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setErrors({});
      setTurnstileToken('');
      turnstileRef.current?.reset();
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setSubmitError('Network error. Please check your connection and try again.');
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Banner */}
      <div className="contact-hero">
        <div className="container">
          <h1>Get in Touch</h1>
          <p>Have a question or want to connect? I'd love to hear from you.</p>
        </div>
      </div>

      <div className="container contact-body">
        <div className="contact-grid">
          {/* Form */}
          <div className="contact-form-section">

            <AnimatePresence>
              {submitted && (
                <motion.div
                  className="success-toast"
                  initial={{ opacity: 0, y: -12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <FaCheck className="success-icon" />
                  Message sent! I'll get back to you soon.
                </motion.div>
              )}
            </AnimatePresence>

            {submitError && (
              <div className="submit-error">
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="contact-form">
              <div className="contact-form-row">
                <div className={`contact-field ${errors.name ? 'has-error' : formData.name ? 'is-valid' : ''}`}>
                  <label htmlFor="name">Name</label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                  />
                  {errors.name && <span className="field-error">{errors.name}</span>}
                </div>
                <div className={`contact-field ${errors.email ? 'has-error' : formData.email && !errors.email ? 'is-valid' : ''}`}>
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                  />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>
              </div>

              <div className={`contact-field ${errors.subject ? 'has-error' : formData.subject ? 'is-valid' : ''}`}>
                <label htmlFor="subject">Subject</label>
                <select id="subject" name="subject" value={formData.subject} onChange={handleChange}>
                  <option value="">Select a subject...</option>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.subject && <span className="field-error">{errors.subject}</span>}
              </div>

              <div className={`contact-field ${errors.message ? 'has-error' : formData.message ? 'is-valid' : ''}`}>
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me what's on your mind..."
                  rows="6"
                />
                {errors.message && <span className="field-error">{errors.message}</span>}
              </div>

              <Turnstile
                ref={turnstileRef}
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onError={() => setSubmitError('Captcha failed to load. Please refresh.')}
                onExpire={() => setTurnstileToken('')}
              />

              <button
                type="submit"
                className="btn contact-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Info panel */}
          <div className="contact-info-panel">
            <h2>Contact Info</h2>
            <p className="contact-info-intro">
              Whether you want advice on dive destinations, gear recommendations, or just want to
              chat about diving — don't hesitate to reach out.
            </p>

            <div className="contact-info-items">
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <FaMapMarkerAlt />
                </div>
                <div>
                  <p className="contact-info-label">Based in</p>
                  <p className="contact-info-value">London, UK</p>
                </div>
              </div>
            </div>

            <div className="contact-social">
              <p className="contact-social-label">Follow along</p>
              <div className="contact-social-links">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="contact-social-btn" aria-label="Instagram">
                  <FaInstagram />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="contact-social-btn" aria-label="Twitter">
                  <FaTwitter />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
